import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FiAlertCircle,
  FiArrowLeft,
  FiBookOpen,
  FiDollarSign,
  FiExternalLink,
  FiHome,
  FiPlus,
  FiClock,
  FiSave,
  FiTarget,
  FiUserCheck,
  FiVideo,
  FiZap,
} from 'react-icons/fi';
import Sidebar from '../Sidebar';
import Editor from '../Editor';
import ReviewWorkspace from '../review/ReviewWorkspace';
import VersionHistoryPanel from '../review/VersionHistoryPanel';
import { API_BASE_URL, getAuthHeaders } from '../../utils/utils';
import { getStoredUser } from '../../utils/authSession';
import './ApplicationPackage.css';

const htmlToPlainText = (value = '') =>
  String(value)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();

const EDITOR_STYLES = {
  wrapper: {
    minHeight: '220px',
    width: '100%',
    border: '1px solid var(--border, #e0e0e0)',
    borderRadius: '12px',
    background: '#fff',
  },
  editorContent: `
    .ap-editor .tiptap {
      min-height: 200px !important;
      max-height: 420px;
      height: auto !important;
      color: var(--foreground, #1d1b20);
    }
    @media (max-width: 800px) {
      .ap-editor .tiptap {
        min-height: 160px !important;
        max-height: 280px;
      }
    }
  `,
};

const KINDS = [
  { value: 'story', label: 'Your story' },
  { value: 'cv', label: 'CV' },
  { value: 'essay', label: 'Essay' },
  { value: 'lor', label: 'Recommendation letter' },
  { value: 'other', label: 'Other' },
];

const emptyDraft = (kind = 'essay') => ({
  kind,
  title: '',
  body: '',
  documentId: null,
});

const pickCommentReview = (document) => {
  const reviews = document?.reviews || [];
  return (
    reviews.find((review) => review.reviewer_type === 'notes') ||
    reviews.find((review) => review.reviewer_type === 'coach') ||
    reviews[0] ||
    null
  );
};

const reviewerLabel = (review) => {
  if (review.reviewer_type === 'ai') {
    return 'Liwaza AI';
  }
  if (review.reviewer_type === 'notes') {
    return 'Comments';
  }
  return review.coach_name || 'Coach';
};

const ApplicationPackage = () => {
  const user = getStoredUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialKind = KINDS.some((kind) => kind.value === searchParams.get('kind'))
    ? searchParams.get('kind')
    : 'essay';
  const [applications, setApplications] = useState([]);
  const [pendingExcerpt, setPendingExcerpt] = useState('');
  const [reviewers, setReviewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTargetId, setActiveTargetId] = useState(null);
  const [draft, setDraft] = useState(() => emptyDraft(initialKind));
  const [coachId, setCoachId] = useState('');
  const [saving, setSaving] = useState(false);
  const [examples, setExamples] = useState([]);
  const [preview, setPreview] = useState(null);
  const [commentsOpen, setCommentsOpen] = useState(
    () => Boolean(searchParams.get('comment_id'))
  );
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const errorRef = useRef(null);
  const reviewsRef = useRef(null);
  const historyRef = useRef(null);

  useEffect(() => {
    const node = errorRef.current;
    if (!error || !node || typeof node.scrollIntoView !== 'function') {
      return;
    }
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [error]);

  const load = async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }
    try {
      const [appsRes, coachesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/applications`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE_URL}/api/application-reviewers`, {
          headers: getAuthHeaders(),
        }),
      ]);
      const appsData = await appsRes.json();
      const coachesData = await coachesRes.json();
      let apps = appsData.applications || [];
      if (!apps.length) {
        apps = await migrateLocalApplications();
      }
      setApplications(apps);
      setReviewers(coachesData.coaches || []);
      setActiveTargetId((current) => current || apps[0]?.id || null);
      return apps;
    } catch (err) {
      setError(err.message || 'Could not load applications');
      return [];
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const kind = searchParams.get('kind');
    if (!KINDS.some((row) => row.value === kind)) {
      return;
    }
    setDraft((prev) => (prev.documentId ? prev : { ...prev, kind }));
  }, [searchParams]);

  useEffect(() => {
    if (!applications.length) {
      return;
    }
    const documentId = Number(searchParams.get('document_id') || 0);
    const targetId = Number(searchParams.get('target_id') || 0);
    const reviewId = Number(searchParams.get('review_id') || 0);
    if (targetId) {
      setActiveTargetId(targetId);
    }
    for (const app of applications) {
      const document = (app.documents || []).find((row) => {
        if (documentId && row.id === documentId) {
          return true;
        }
        return Boolean(
          reviewId && (row.reviews || []).some((review) => review.id === reviewId)
        );
      });
      if (document) {
        setActiveTargetId(app.id);
        setDraft({
          kind: document.kind,
          title: document.title,
          body: document.body || '',
          documentId: document.id,
        });
        if (searchParams.get('comment_id')) {
          setCommentsOpen(true);
        }
        break;
      }
    }
  }, [applications, searchParams]);

  useEffect(() => {
    let cancelled = false;
    const loadExamples = async () => {
      if (!['cv', 'essay', 'story', 'lor'].includes(draft.kind)) {
        if (!cancelled) {
          setExamples([]);
        }
        return;
      }
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/application-examples?kind=${encodeURIComponent(draft.kind)}`,
          { headers: getAuthHeaders() }
        );
        const data = await response.json();
        if (!cancelled) {
          setExamples(data.examples || []);
        }
      } catch {
        if (!cancelled) {
          setExamples([]);
        }
      }
    };
    loadExamples();
    return () => {
      cancelled = true;
    };
  }, [draft.kind]);

  const previewExample = async (example) => {
    try {
      const response = await fetch(`${API_BASE_URL}${example.preview_path}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Could not open the example');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      if (example.is_pdf) {
        setPreview({
          title: example.title,
          url,
          filename: example.original_filename,
        });
        return;
      }
      const link = document.createElement('a');
      link.href = url;
      link.download = example.original_filename || 'example.docx';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Could not open the example');
    }
  };

  const closePreview = () => {
    if (preview?.url) {
      URL.revokeObjectURL(preview.url);
    }
    setPreview(null);
  };

  const active = useMemo(
    () => applications.find((row) => row.id === activeTargetId) || applications[0],
    [applications, activeTargetId]
  );

  const toolbarCommentCount = useMemo(() => {
    if (!draft.documentId) {
      return 0;
    }
    const document = (active?.documents || []).find((row) => row.id === draft.documentId);
    return (document?.reviews || []).reduce(
      (count, review) => count + (review.comments || []).length,
      0
    );
  }, [active, draft.documentId]);

  const currentDocumentHasReviews = Boolean(
    draft.documentId &&
      (active?.documents || []).some(
        (doc) => doc.id === draft.documentId && doc.reviews?.length
      )
  );

  const openComments = (payload = {}) => {
    const excerpt = String(payload?.excerpt || '').replace(/\s+/g, ' ').trim();
    if (excerpt) {
      setPendingExcerpt(excerpt);
    }
    setCommentsOpen(true);
    if (
      currentDocumentHasReviews &&
      reviewsRef.current &&
      typeof reviewsRef.current.scrollIntoView === 'function'
    ) {
      setError('');
      reviewsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (currentDocumentHasReviews) {
      setError('');
      return;
    }
    setError('Request a Liwaza AI or coach review to start commenting.');
  };

  const persistDocument = async ({ title } = {}) => {
    if (!active) {
      throw new Error('Choose a school target first');
    }
    const nextTitle = (title ?? draft.title).trim() || 'Untitled draft';
    const response = await fetch(`${API_BASE_URL}/api/application-documents`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        target_id: active.id,
        kind: draft.kind,
        title: nextTitle,
        body: draft.body,
        document_id: draft.documentId,
      }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || 'Save failed');
    setDraft((prev) => ({
      ...prev,
      title: nextTitle,
      documentId: data.document.id,
    }));
    await loadVersions(data.document.id);
    return data.document.id;
  };

  useEffect(() => {
    loadVersions(draft.documentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.documentId]);

  const saveDocument = async () => {
    if (!active || !draft.title.trim()) {
      setError('Give the document a title');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const documentId = await persistDocument();
      await load({ silent: true });
      return documentId;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const submitInlineComment = async (payload) => {
    if (!htmlToPlainText(draft.body)) {
      setError('Write or paste a document before commenting');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const documentId = await persistDocument();
      const apps = await load({ silent: true });
      const target =
        apps.find((row) => row.id === activeTargetId) ||
        apps.find((row) => row.id === active?.id) ||
        apps[0];
      const document = (target?.documents || []).find((row) => row.id === documentId);
      let review = pickCommentReview(document);
      if (!review) {
        const response = await fetch(
          `${API_BASE_URL}/api/application-documents/${documentId}/review`,
          {
            method: 'POST',
            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ reviewer: 'notes' }),
          }
        );
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || 'Could not start comments');
        review = data.review;
      }
      await postReviewAction(`/api/review-requests/${review.id}/comments`, {
        excerpt: payload.excerpt,
        comment: payload.comment,
        start_offset: payload.start_offset,
        end_offset: payload.end_offset,
        assigned_user_id: payload.assigned_user_id,
        suggestion_text: payload.suggestion_text,
        mentioned_user_ids: payload.mentioned_user_ids,
      });
      setCommentsOpen(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const requestReview = async (reviewer) => {
    let documentId = draft.documentId;
    if (!documentId) {
      if (!draft.title.trim() || !htmlToPlainText(draft.body)) {
        setError('Write or paste a document before requesting a review');
        return;
      }
      documentId = await saveDocument();
    }
    if (!documentId) return;
    if (reviewer === 'coach' && !coachId) {
      setError('Select a review coach');
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/application-documents/${documentId}/review`,
        {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            reviewer,
            coach_id: reviewer === 'coach' ? Number(coachId) : undefined,
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Review failed');
      await load({ silent: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const postReviewAction = async (path, payload) => {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: payload ? JSON.stringify(payload) : undefined,
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.detail || 'Could not save comment');
    }
    if (data.document?.body != null) {
      setDraft((prev) => ({
        ...prev,
        body: data.document.body,
        title: data.document.title || prev.title,
      }));
    }
    await load({ silent: true });
    return data;
  };

  const loadVersions = async (documentId) => {
    if (!documentId) {
      setVersions([]);
      return [];
    }
    setVersionsLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/application-documents/${documentId}/versions`,
        { headers: getAuthHeaders() }
      );
      const data = await response.json();
      const rows = data.versions || [];
      setVersions(rows);
      return rows;
    } catch (_err) {
      setVersions([]);
      return [];
    } finally {
      setVersionsLoading(false);
    }
  };

  const openDocument = (document) => {
    setDraft({
      kind: document.kind,
      title: document.title,
      body: document.body || '',
      documentId: document.id,
    });
    if (document.reviews?.length) {
      setError('');
    }
  };

  return (
    <div className="ap-wrapper">
      <Sidebar />
      <div className="ap-container">
        <header className="ap-header">
          <nav className="ap-breadcrumb" aria-label="Application package">
            <button
              type="button"
              className="ap-back"
              onClick={() => navigate('/home#targeting')}
            >
              <FiArrowLeft size={16} />
              Update school targets
            </button>
          </nav>
          <div>
            <h1>{user?.first_name}, your Application Package</h1>
            <p>
              Write or paste your story, CV, essays, and recommendation letters
              for each school × program. Ask Liwaza AI or a review coach for
              comments. Nothing is gated.
            </p>
          </div>
        </header>

        {error && !applications.length ? (
          <p ref={errorRef} className="ap-error" role="alert">
            <FiAlertCircle size={18} aria-hidden="true" />
            {error}
          </p>
        ) : null}

        {loading ? <p>Loading your package…</p> : null}
        {!loading && !applications.length ? (
          <div className="ap-empty">
            <FiBookOpen size={40} />
            <h3>No targets yet</h3>
            <p>Answer the Home questions and choose 3 school × program combinations.</p>
            <button
              type="button"
              className="btn-lwz-primary"
              onClick={() => navigate('/home#targeting')}
            >
              <FiTarget /> Choose targets on Home
            </button>
          </div>
        ) : null}

        {applications.length ? (
          <div className="ap-layout">
            <aside className="ap-targets">
              {applications.map((app) => (
                <button
                  type="button"
                  key={app.id}
                  className={app.id === active?.id ? 'is-active' : ''}
                  onClick={() => {
                    setActiveTargetId(app.id);
                    setDraft(emptyDraft(initialKind));
                  }}
                >
                  <strong>
                    #{app.priority} {app.program?.school_name}
                  </strong>
                  <span>{app.program?.name}</span>
                  {app.priority === 1 ? <em>Tracking choice</em> : null}
                </button>
              ))}
            </aside>

            {active ? (
              <section className="ap-workspace">
                <div className="ap-school">
                  <div>
                    <p className="ap-kicker">
                      #{active.priority} {active.priority === 1 ? '· Tracking choice' : ''}
                    </p>
                    <h2>
                      {active.program?.school_name} · {active.program?.name}
                    </h2>
                    {active.program?.ranking ? (
                      <p>
                        Rank #{active.program.ranking.value}{' '}
                        {active.program.ranking.source_name}{' '}
                        <a
                          href={active.program.ranking.source_url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          source <FiExternalLink size={12} />
                        </a>
                      </p>
                    ) : null}
                  </div>
                  <div className="ap-cta">
                    <button
                      type="button"
                      className="btn-lwz-secondary"
                      onClick={() => {
                        const params = [];
                        if (active.program?.school_name) {
                          params.push(
                            `school=${encodeURIComponent(active.program.school_name)}`
                          );
                        }
                        if (active.program?.name) {
                          params.push(
                            `program=${encodeURIComponent(active.program.name)}`
                          );
                        }
                        navigate(
                          params.length
                            ? `/interview?${params.join('&')}`
                            : '/interview'
                        );
                      }}
                    >
                      <FiVideo size={18} />
                      Prepare your interviews
                    </button>
                    <button
                      type="button"
                      className="btn-lwz-secondary"
                      onClick={() =>
                        navigate(
                          active?.id ? `/funding?target_id=${active.id}` : '/funding'
                        )
                      }
                    >
                      <FiDollarSign size={18} />
                      Build your funding strategy
                    </button>
                  </div>
                </div>

                <div className="ap-work">
                  <div className="ap-docs">
                  <div className="ap-docs__header">
                    <h3>Documents</h3>
                    <button
                      type="button"
                      className="ap-ghost"
                      onClick={() => setDraft(emptyDraft(initialKind))}
                    >
                      <FiPlus size={14} /> New draft
                    </button>
                  </div>
                  <div className="ap-doc-list">
                    {(active.documents || []).map((doc) => (
                      <button
                        type="button"
                        key={doc.id}
                        className={doc.id === draft.documentId ? 'is-active' : ''}
                        aria-current={doc.id === draft.documentId ? 'true' : undefined}
                        onClick={() => openDocument(doc)}
                      >
                        <strong>{doc.title}</strong>
                        <span>
                          {KINDS.find((kind) => kind.value === doc.kind)?.label || doc.kind}
                          {doc.reviews?.length ? ` · ${doc.reviews.length} review(s)` : ''}
                        </span>
                      </button>
                    ))}
                    {!draft.documentId ? (
                      <button
                        type="button"
                        className="is-active is-draft"
                        aria-current="true"
                      >
                        <strong>{draft.title.trim() || 'Untitled draft'}</strong>
                        <span>
                          {KINDS.find((kind) => kind.value === draft.kind)?.label ||
                            draft.kind}{' '}
                          · not saved yet
                        </span>
                      </button>
                    ) : null}
                  </div>
                  {(active.documents || []).length === 0 ? (
                    <p>No saved documents yet. Write or paste below.</p>
                  ) : (
                    <p className="ap-docs__hint">
                      Saved documents stay listed here when you start a new draft.
                    </p>
                  )}
                </div>

                <div className="ap-editor">
                  <div className="ap-editor-row">
                    <select
                      value={draft.kind}
                      onChange={(event) =>
                        setDraft((prev) => ({ ...prev, kind: event.target.value }))
                      }
                    >
                      {KINDS.map((kind) => (
                        <option key={kind.value} value={kind.value}>
                          {kind.label}
                        </option>
                      ))}
                    </select>
                    <input
                      value={draft.title}
                      onChange={(event) =>
                        setDraft((prev) => ({ ...prev, title: event.target.value }))
                      }
                      placeholder="Document title"
                    />
                  </div>
                  <Editor
                    value={draft.body}
                    onChange={(body) => setDraft((prev) => ({ ...prev, body }))}
                    placeholder="Write here or paste a draft for correction."
                    customStyles={EDITOR_STYLES}
                    showToolbar
                    onComment={openComments}
                    onAddComment={submitInlineComment}
                    commentCount={toolbarCommentCount}
                    commentsActive={commentsOpen}
                    commentSaving={saving}
                  />
                  <div className="ap-editor-actions">
                    <div className="ap-editor-toolbar">
                      <button
                        type="button"
                        className="btn-lwz-primary"
                        disabled={saving}
                        onClick={saveDocument}
                      >
                        <FiSave /> {saving ? 'Saving…' : 'Save draft'}
                      </button>
                      <button
                        type="button"
                        className={`ap-history-btn${historyOpen ? ' is-active' : ''}`}
                        aria-pressed={historyOpen}
                        onClick={async () => {
                          const next = !historyOpen;
                          setHistoryOpen(next);
                          if (next) {
                            await loadVersions(draft.documentId);
                            historyRef.current?.scrollIntoView?.({
                              behavior: 'smooth',
                              block: 'start',
                            });
                          }
                        }}
                      >
                        <FiClock /> Version history
                      </button>
                    </div>
                    {historyOpen ? (
                      <div ref={historyRef}>
                        <VersionHistoryPanel
                          versions={versions}
                          loading={versionsLoading}
                          currentTitle={draft.title}
                          currentBody={draft.body}
                          saving={saving}
                          onClose={() => setHistoryOpen(false)}
                          onRestore={async (versionId) => {
                            if (!draft.documentId) {
                              setError('Save the draft before restoring a version');
                              return;
                            }
                            try {
                              await postReviewAction(
                                `/api/application-documents/${draft.documentId}/versions/${versionId}/restore`
                              );
                              await loadVersions(draft.documentId);
                            } catch (err) {
                              setError(err.message);
                            }
                          }}
                        />
                      </div>
                    ) : null}
                    <div className="ap-review-actions">
                      <p className="ap-review-actions__label">Get a review</p>
                      <div className="ap-review-actions__grid">
                        <button
                          type="button"
                          className="btn-lwz-secondary"
                          disabled={saving}
                          aria-label="Request Liwaza AI review"
                          onClick={() => requestReview('ai')}
                        >
                          <FiZap size={16} /> Ask Liwaza AI
                        </button>
                        <div className="ap-coach-action">
                          <select
                            value={coachId}
                            aria-label="Select a review coach"
                            onChange={(event) => setCoachId(event.target.value)}
                          >
                            <option value="">Select a review coach</option>
                            {reviewers.map((coach) => (
                              <option key={coach.id} value={coach.id}>
                                {coach.name || `Coach #${coach.id}`}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="btn-lwz-outline"
                            disabled={saving || !coachId}
                            aria-label="Request coach review"
                            title={coachId ? 'Send this draft to the selected coach' : 'Select a review coach'}
                            onClick={() => requestReview('coach')}
                          >
                            <FiUserCheck size={16} /> Ask coach
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  {error ? (
                    <p ref={errorRef} className="ap-error" role="alert">
                      <FiAlertCircle size={18} aria-hidden="true" />
                      {error}
                    </p>
                  ) : null}
                  {examples.length ? (
                    <div className="ap-examples">
                      <h3>
                        Example{' '}
                        {KINDS.find((kind) => kind.value === draft.kind)?.label ||
                          draft.kind}
                        s
                      </h3>
                      <p>
                        Preview a model {draft.kind === 'cv' ? 'CV' : 'document'}{' '}
                        uploaded by Liwaza. Open the PDF here, or download a Word
                        file.
                      </p>
                      <div className="ap-example-list">
                        {examples.map((example) => (
                          <article key={example.id} className="ap-example">
                            <strong>{example.title}</strong>
                            {example.description ? <p>{example.description}</p> : null}
                            <button
                              type="button"
                              className="ap-ghost"
                              onClick={() => previewExample(example)}
                            >
                              {example.is_pdf ? 'Preview example' : 'Download example'}
                            </button>
                          </article>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
                </div>

                {(active.documents || []).some((doc) => doc.reviews?.length) ? (
                  <div className="ap-reviews" ref={reviewsRef} id="ap-reviews">
                    <h3>Reviews</h3>
                    {(active.documents || [])
                      .filter((doc) => !draft.documentId || doc.id === draft.documentId)
                      .filter((doc) => doc.reviews?.length)
                      .map((doc) => {
                      const comments = (doc.reviews || []).flatMap((review) =>
                        (review.comments || []).map((comment) => ({
                          ...comment,
                          review_id: review.id,
                        }))
                      );
                      const reviewId =
                        Number(searchParams.get('review_id') || 0) ||
                        (doc.reviews || []).find((review) => review.reviewer_type === 'coach')?.id ||
                        (doc.reviews || [])[0]?.id;
                      return (
                        <article key={doc.id} className="ap-review">
                          <header className="ap-review__header">
                            <div>
                              <strong>{doc.title}</strong>
                              <span className="ap-review__doc">
                                {(doc.reviews || [])
                                  .map((review) => reviewerLabel(review))
                                  .join(' · ')}
                              </span>
                              {(doc.reviews || []).map((review) =>
                                review.summary ? (
                                  <p key={`${review.id}-summary`} className="ap-review__summary">
                                    {review.summary}
                                  </p>
                                ) : null
                              )}
                            </div>
                          </header>
                          {comments.length ? (
                            <ReviewWorkspace
                              body={doc.body || draft.body}
                              comments={comments}
                              selectedCommentId={Number(searchParams.get('comment_id') || 0) || null}
                              focusComments={commentsOpen}
                              pendingExcerpt={pendingExcerpt}
                              canComment={Boolean(reviewId)}
                              layout="stack"
                              saving={saving}
                              currentUserId={user?.id}
                              collaborators={(doc.reviews || []).find((row) => row.id === reviewId)?.collaborators || []}
                              versions={versions}
                              versionsLoading={versionsLoading}
                              onSubmitComment={async (payload) => {
                                const targetReview = payload.review_id || reviewId;
                                if (!targetReview) {
                                  setError('Request a review before commenting');
                                  return;
                                }
                                try {
                                  setPendingExcerpt('');
                                  await postReviewAction(
                                    `/api/review-requests/${targetReview}/comments`,
                                    {
                                      excerpt: payload.excerpt,
                                      comment: payload.comment,
                                      parent_id: payload.parent_id,
                                      start_offset: payload.start_offset,
                                      end_offset: payload.end_offset,
                                      assigned_user_id: payload.assigned_user_id,
                                      suggestion_text: payload.suggestion_text,
                                      mentioned_user_ids: payload.mentioned_user_ids,
                                    }
                                  );
                                } catch (err) {
                                  setError(err.message);
                                }
                              }}
                              onResolve={(commentId) =>
                                postReviewAction(
                                  `/api/review-requests/${reviewId}/comments/${commentId}/resolve`
                                )
                              }
                              onReopen={(commentId) =>
                                postReviewAction(
                                  `/api/review-requests/${reviewId}/comments/${commentId}/reopen`
                                )
                              }
                              onReact={(commentId, emoji) =>
                                postReviewAction(
                                  `/api/review-requests/${reviewId}/comments/${commentId}/react`,
                                  { emoji }
                                )
                              }
                              onAssign={(commentId, assignedUserId) =>
                                postReviewAction(
                                  `/api/review-requests/${reviewId}/comments/${commentId}/assign`,
                                  { assigned_user_id: assignedUserId }
                                )
                              }
                              onAcceptSuggestion={(commentId) =>
                                postReviewAction(
                                  `/api/review-requests/${reviewId}/comments/${commentId}/accept-suggestion`
                                )
                              }
                              onRejectSuggestion={(commentId) =>
                                postReviewAction(
                                  `/api/review-requests/${reviewId}/comments/${commentId}/reject-suggestion`
                                )
                              }
                              onRestoreVersion={async (versionId) => {
                                if (!draft.documentId) {
                                  return;
                                }
                                await postReviewAction(
                                  `/api/application-documents/${draft.documentId}/versions/${versionId}/restore`
                                );
                                await loadVersions(draft.documentId);
                              }}
                            />
                          ) : (
                            <p className="ap-review__pending">
                              Comments will appear here as soon as the coach writes them.
                            </p>
                          )}
                        </article>
                      );
                    })}
                  </div>
                ) : null}
              </section>
            ) : null}
          </div>
        ) : null}
      </div>
      {preview ? (
        <div className="ap-preview-overlay" onClick={closePreview}>
          <div
            className="ap-preview"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-label={preview.title}
          >
            <header>
              <strong>{preview.title}</strong>
              <button type="button" className="ap-ghost" onClick={closePreview}>
                Close
              </button>
            </header>
            <iframe title={preview.title} src={preview.url} />
          </div>
        </div>
      ) : null}
    </div>
  );
};

async function migrateLocalApplications() {
  try {
    const raw = localStorage.getItem('liwaza-applications');
    if (!raw) return [];
    const localApps = JSON.parse(raw);
    if (!Array.isArray(localApps) || !localApps.length) return [];
    const programsRes = await fetch(`${API_BASE_URL}/api/programs`, {
      headers: getAuthHeaders(),
    });
    const programsData = await programsRes.json();
    const catalog = programsData.programs || [];
    const matchedIds = [];
    localApps.forEach((app) => {
      const match = catalog.find(
        (program) =>
          program.school_name === app.schoolName &&
          (!app.programName || program.name === app.programName)
      );
      if (match && !matchedIds.includes(match.id)) {
        matchedIds.push(match.id);
      }
    });
    if (matchedIds.length < 3) {
      catalog.forEach((program) => {
        if (matchedIds.length < 3 && !matchedIds.includes(program.id)) {
          matchedIds.push(program.id);
        }
      });
    }
    if (matchedIds.length !== 3) return [];
    const targeting = await fetch(`${API_BASE_URL}/api/targeting`, {
      headers: getAuthHeaders(),
    });
    const targetingJson = targeting.ok
      ? await targeting.json()
      : { current_study: '', future_goal: '' };
    const saveRes = await fetch(`${API_BASE_URL}/api/targeting`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        current_study: targetingJson.current_study || 'Existing Liwaza applicant',
        current_level: targetingJson.current_level || 'other',
        future_goal: targetingJson.future_goal || 'Graduate program',
        program_ids: matchedIds.slice(0, 3),
      }),
    });
    if (!saveRes.ok) return [];
    const appsRes = await fetch(`${API_BASE_URL}/api/applications`, {
      headers: getAuthHeaders(),
    });
    const appsData = await appsRes.json();
    return appsData.applications || [];
  } catch {
    return [];
  }
}

export default ApplicationPackage;
